import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { SAFETY_PILLARS } from './data';

// SAFETY — long-form "audit dossier" treatment with numbered references.
// Each pillar reads like an internal procedure document.
export default function Safety({ setPage }) {
  return (
    <div className="bg-[#0F0F11] text-[#EAF1F4]">
      <section className="pt-14 pb-12 max-w-[1500px] mx-auto px-6 lg:px-10 border-b border-[#EAF1F4]/10">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-4">⏵ Section 03 / Safety File · STR-SMS-2026</p>
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10 items-end">
          <h1 className="font-serif text-5xl lg:text-8xl leading-[0.9] tracking-[-0.025em]">A document. <span className="italic text-[#D73B26]">Open</span>.</h1>
          <div className="space-y-3 text-sm text-[#EAF1F4]/65 leading-[1.7] max-w-md">
            <p>Other operators publish brochures. We publish the dossier our auditors review. The four pillars below are extracted, in plain language, from our SMS manual.</p>
            <p className="text-[#D73B26] font-mono text-[10px] tracking-[0.3em] uppercase">Last audited 14 Nov 2025 — ARG/US Platinum, current.</p>
          </div>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20 space-y-16">
        {SAFETY_PILLARS.map((p, i) => (
          <motion.article key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            className="grid lg:grid-cols-[120px_1fr] gap-6 lg:gap-12 border-l-2 border-[#D73B26]/40 pl-6 lg:pl-10">
            <div>
              <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#D73B26]">/ Pillar</p>
              <p className="font-serif text-7xl lg:text-8xl text-[#D73B26] leading-none">{p.id}</p>
              <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#EAF1F4]/40 mt-2">REF · {p.n}</p>
            </div>
            <div>
              <h2 className="font-serif text-3xl lg:text-5xl leading-tight">{p.t}</h2>
              <p className="text-base lg:text-lg text-[#EAF1F4]/75 leading-[1.7] mt-4">{p.d}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-xs font-mono tracking-[0.25em] uppercase text-[#5FFB7A]"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Verified externally</div>
            </div>
          </motion.article>
        ))}
      </section>

      {/* Auditor logos as a strict grid (NOT a marquee) */}
      <section className="bg-[#06060A] border-y border-[#EAF1F4]/10 py-16">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-8">⏵ Independent auditors</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#EAF1F4]/10">
            {['ARG/US Platinum', 'Wyvern Wingman', 'IS-BAO Stage III', 'EASA Part-CAT'].map(a => (
              <div key={a} className="bg-[#06060A] py-10 px-6 flex items-center justify-center text-center">
                <span className="font-serif text-xl lg:text-2xl text-[#EAF1F4]/85">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 max-w-[820px] mx-auto px-6 text-center">
        <p className="font-serif text-3xl lg:text-5xl leading-[1.15]">If a captain says no, <span className="italic text-[#D73B26]">we say no</span>.</p>
        <p className="text-base text-[#EAF1F4]/65 mt-6 leading-[1.7]">We have refunded six departures in 2024 because a captain elected against weather. We will refund every one of them, every time.</p>
        <button onClick={() => setPage('quote')} className="mt-10 bg-[#D73B26] text-[#0F0F11] px-8 py-4 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#EAF1F4] transition-colors">Request a quote →</button>
      </section>
    </div>
  );
}
